<template>
  <div v-if="!loading" :class="['col-12 flex no-box py2 items-center flex-column white-bg', { 'px1': !removePadding }]">
    <!-- DESKTOP -->
    <div :class="['xs-hide sm-hide flex col-12', { 'px1': !removePadding }]">
      <form class="flex flex-column flex-grow" @submit.prevent="submitGymlibCodeRequest">
        <div class="flex col-12 pr1 flex-grow mt1">
          <input v-model="gymlibCode" type="search" placeholder="eGym Wellpass" class="h5n flex-grow p2">
          <button
            type="submit"
            class="no-wrap h5n transparent-bg p2 border-none bold"
            :class="[btnColor, {'disabled': !gymlibCode}]"
          >
            {{ label }}
          </button>
        </div>
        <div class="flex flex-wrap justify-center">
          <div v-if="!codeValid && message" class="flex flex-column col-12 mt1 red justify-center center h5n">
            <div v-for="msg in parsedMessages" :key="msg">
              {{ msg }}
            </div>
          </div>
          <div v-else-if="codeValid" class="flex col-12 mt1 green justify-center">Code appliqué</div>
        </div>
      </form>
    </div>
    <!-- MOBILE -->
    <div :class="['md-hide lg-hide flex col-12', { 'px1': !removePadding }]">
      <form class="flex flex-column flex-grow" @submit.prevent="submitGymlibCodeRequest">
        <div class="flex col-12 pr1 flex-grow mt1">
          <input v-model="gymlibCode" type="search" placeholder="eGym Wellpass" class="h6 flex-grow p15">
          <button
            type="submit"
            class="no-wrap h6 transparent-bg p15 border-none bold"
            :class="[btnColor, {'disabled': !gymlibCode}]"
          >
            {{ label }}
          </button>
        </div>
        <div class="flex flex-wrap justify-center">
          <div v-if="!codeValid && message" class="flex flex-column col-12 mt1 red justify-center center h6">
            <div v-for="msg in parsedMessages" :key="msg">
              {{ msg }}
            </div>
          </div>
          <div v-else-if="codeValid" class="flex col-12 mt1 green justify-center">Code appliqué</div>
        </div>
      </form>
    </div>
  </div>
  <div v-else class="flex col-12 white-bg p2 justify-center items-center">
    <div class="spinner"></div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { submitGymlibCode } from '@/utils/requests';
import { useStore } from '@/store/index';

const props = defineProps({
  price: { type: Number },
  removePadding: { type: Boolean, default: false },
  btnColor: { type: String, default: 'invert' },
  label: { type: String, default: 'Valider' }
});

const emits = defineEmits(['validate-code', 'gym-lib-code-changed']);

const store = useStore();

const loading = ref(false);
const codeValid = ref(false);
const gymlibCode = ref(null);
const message = ref(null);
const passAlreadyUsed = ref(false);
const codeInvalid = ref(false);

const courtSession = computed(() => {
  if (store.state.courtSettings && store.state.courtSettings.session) {
    return {
      ...store.state.courtSettings.session,
      start_time: store.state.courtSettings.session.start_time * 1000,
      end_time: store.state.courtSettings.session.end_time * 1000
    };
  }
  return null;
});
const workspace = computed(() => store.state.siteInfo.workspace);
const currentParticipant = computed(() => store.state.currentParticipant);
const gymlibCodes = computed(() => {
  const codes = [];
  // Check participants (for subscriptions)
  (store.state.cartItem?.participants || []).forEach(p => {
    if (p.tariff && p.tariff.gymlibCode) codes.push(p.tariff.gymlibCode);
  });
  // Check tariffs (for unit sales)
  (store.state.cartItem?.tariffs || []).forEach(t => {
    if (t.gymlibCode) codes.push(t.gymlibCode);
  });
  return codes;
});

const submitGymlibCodeRequest = async () => {
  loading.value = true;
  message.value = null;
  codeValid.value = false;
  passAlreadyUsed.value = false;
  codeInvalid.value = false;

  // Check if code is already used in OTHER tariffs/participants (not the current one being validated)
  // Only check codes that are different from what's currently stored in the current tariff
  const currentTariff = store.state.cartItem?.tariffs?.find(t => {
    const price = store.state.offerInfo?.detail?.prices?.find(p => p.tariffCode === t.tariffCode);
    return price && price.isGymlibTariff === true;
  });

  // Only flag as already used if the code exists in OTHER items (not the current tariff)
  const isCodeInOtherItems = gymlibCodes.value.some(code => {
    // If current tariff already has this exact code, it's the same validation, so allow it
    if (currentTariff && currentTariff.gymlibCode === gymlibCode.value) {
      return false;
    }
    // Otherwise, if code matches, it's used elsewhere
    return code === gymlibCode.value;
  });

  if (isCodeInOtherItems) {
    passAlreadyUsed.value = true;
    loading.value = false;
    return;
  }

  // Get start_time from session, handling both formats (start_time or startTime)
  const session = store.state.cartItem?.session || courtSession.value;
  const sessionStartTime = session ? (session.start_time || session.startTime) : null;
  const sessionEndTime = session ? (session.end_time || session.endTime) : null;

  await submitGymlibCode({ productUuid: store.state.product.uuid, code: gymlibCode.value, customerUuid: currentParticipant.value.uuid, workspace: workspace.value, price: props.price, startTime: sessionStartTime, endTime: sessionEndTime, gymlibAmount: props.price }).then(r => {
    codeValid.value = r.success;
    if (!r.success) codeInvalid.value = true;
    message.value = r.message;
    store.commit('setCurrentParticipantGymlibCode', `${gymlibCode.value}`);
  }).finally(() => {
    loading.value = false;
  });
};

// Watch for tariff changes to restore code when navigating back
const currentTariffRef = computed(() => store.state.cartItem?.tariffs?.find(t => {
  const price = store.state.offerInfo?.detail?.prices?.find(p => p.tariffCode === t.tariffCode);
  return price && price.isGymlibTariff === true;
}));

const parsedMessages = computed(() => (message.value ? message.value.split('\n') : null));

// Watch for tariff changes to restore code when navigating back
watch(currentTariffRef, tariff => {
  if (tariff && tariff.gymlibCode) {
    // Restore code if it's different or if we don't have one yet
    if (tariff.gymlibCode !== gymlibCode.value) {
      gymlibCode.value = tariff.gymlibCode;
    }
    // Restore validation state if code was already validated
    if (tariff.gymlibCodeValid && !codeValid.value) {
      codeValid.value = true;
      emits('validate-code', true);
    }
  } else if (!tariff) {
    // If tariff is removed, clear the code
    gymlibCode.value = null;
    codeValid.value = false;
  }
}, { immediate: true });

watch(codeValid, () => {
  emits('validate-code', codeValid.value);
  // Only emit code changed when validation is successful
  if (codeValid.value) {
    emits('gym-lib-code-changed', gymlibCode.value);
  }
});

watch(gymlibCode, () => {
  // Don't emit on every keystroke, only when code is cleared or changed significantly
  // The code will be emitted after successful validation
});

</script>
